import { makeAutoObservable } from "mobx";

export interface EmailLogEntry {
  type: string;
  to: string;
  subject: string;
  sentAt: string;
}

export interface TrialRequest {
  id: string;
  requesterEmail: string;
  firstName: string;
  lastName: string;
  company: string;
  country: string;
  role: string;
  featureInterest: string;
  reportIntent: string;
  isDuplicate: boolean;
  trialLicenseKey?: string;
  trialLicenseExpirationDate?: string;
  trialLicenseExpirationTimestamp?: number;
  emailsSent: EmailLogEntry[];
  createdAt: string;
}

export interface LeadSummary {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  country: string;
  company: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  actionCount: number;
}

export interface LeadAction {
  leadSource: string;
  actionCode: string;
  offerTitle: string;
  description: string;
  timestamp: number;
  raw: string;
  createdAt: string;
}

export interface Lead {
  id: string;
  email: string;
  randomHash: string;
  firstName: string;
  lastName: string;
  phone: string;
  country: string;
  company: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  actions: LeadAction[];
}

export interface AdminAction {
  leadEmail: string;
  leadFirstName: string;
  leadLastName: string;
  leadCompany: string;
  leadSource: string;
  actionCode: string;
  offerTitle: string;
  description: string;
  createdAt: string;
  raw: string;
}

export type AdminTab = "trial-requests" | "leads" | "actions";

interface PaginatedState<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

const TOKEN_KEY = "innofalls_admin_token";
const DEFAULT_PAGE_SIZE = 25;

function emptyPaginatedState<T>(): PaginatedState<T> {
  return { items: [], total: 0, page: 1, pageSize: DEFAULT_PAGE_SIZE };
}

export class AdminStore {
  token: string | null = sessionStorage.getItem(TOKEN_KEY);
  pinRequested = false;
  pin = "";
  loading = false;
  error = "";
  activeTab: AdminTab = "trial-requests";

  trialRequests = emptyPaginatedState<TrialRequest>();
  leads = emptyPaginatedState<LeadSummary>();
  actions = emptyPaginatedState<AdminAction>();

  selectedRequest: TrialRequest | null = null;
  selectedLead: Lead | null = null;
  selectedAction: AdminAction | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  get isAuthenticated(): boolean {
    return Boolean(this.token);
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      sessionStorage.setItem(TOKEN_KEY, token);
    } else {
      sessionStorage.removeItem(TOKEN_KEY);
    }
  }

  logout() {
    this.setToken(null);
    this.trialRequests = emptyPaginatedState();
    this.leads = emptyPaginatedState();
    this.actions = emptyPaginatedState();
    this.selectedRequest = null;
    this.selectedLead = null;
    this.selectedAction = null;
    this.pin = "";
    this.pinRequested = false;
    this.error = "";
    this.activeTab = "trial-requests";
  }

  setActiveTab(tab: AdminTab) {
    this.activeTab = tab;
    this.selectedRequest = null;
    this.selectedLead = null;
    this.selectedAction = null;
    this.error = "";
    void this.loadActiveTab();
  }

  async loadActiveTab(page?: number) {
    switch (this.activeTab) {
      case "trial-requests":
        return this.loadTrialRequests(page);
      case "leads":
        return this.loadLeads(page);
      case "actions":
        return this.loadActions(page);
    }
  }

  async refreshActiveTab() {
    const page = this.getActivePageState().page;
    return this.loadActiveTab(page);
  }

  private getActivePageState(): PaginatedState<unknown> {
    switch (this.activeTab) {
      case "trial-requests":
        return this.trialRequests;
      case "leads":
        return this.leads;
      case "actions":
        return this.actions;
    }
  }

  private authHeaders(): HeadersInit {
    return {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.token}`,
    };
  }

  private async fetchPaginated<T>(
    url: string,
    page: number,
    pageSize: number,
  ): Promise<PaginatedResponse<T> | null> {
    if (!this.token) {
      return null;
    }
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    const response = await fetch(`${url}?${params.toString()}`, {
      headers: this.authHeaders(),
    });
    if (response.status === 401) {
      this.logout();
      throw new Error("Session expired. Please request a new PIN.");
    }
    if (!response.ok) {
      throw new Error("Failed to load data");
    }
    return response.json();
  }

  async requestPin(): Promise<boolean> {
    this.loading = true;
    this.error = "";
    try {
      const response = await fetch("/admin/auth/request-pin", { method: "POST" });
      if (!response.ok) {
        throw new Error("Failed to request PIN");
      }
      this.pinRequested = true;
      return true;
    } catch (e) {
      this.error = e instanceof Error ? e.message : "Failed to request PIN";
      return false;
    } finally {
      this.loading = false;
    }
  }

  async verifyPin(): Promise<boolean> {
    this.loading = true;
    this.error = "";
    try {
      const response = await fetch("/admin/auth/verify-pin", {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ pin: this.pin }),
      });
      if (!response.ok) {
        throw new Error("Invalid or expired PIN");
      }
      const data: { token: string } = await response.json();
      this.setToken(data.token);
      this.pin = "";
      return true;
    } catch (e) {
      this.error = e instanceof Error ? e.message : "Invalid or expired PIN";
      return false;
    } finally {
      this.loading = false;
    }
  }

  async loadTrialRequests(page = this.trialRequests.page): Promise<boolean> {
    this.loading = true;
    this.error = "";
    try {
      const data = await this.fetchPaginated<TrialRequest>(
        "/admin/trial-requests",
        page,
        this.trialRequests.pageSize,
      );
      if (!data) {
        return false;
      }
      this.trialRequests = data;
      return true;
    } catch (e) {
      this.error = e instanceof Error ? e.message : "Failed to load trial requests";
      return false;
    } finally {
      this.loading = false;
    }
  }

  async loadLeads(page = this.leads.page): Promise<boolean> {
    this.loading = true;
    this.error = "";
    try {
      const data = await this.fetchPaginated<LeadSummary>(
        "/admin/leads",
        page,
        this.leads.pageSize,
      );
      if (!data) {
        return false;
      }
      this.leads = data;
      return true;
    } catch (e) {
      this.error = e instanceof Error ? e.message : "Failed to load leads";
      return false;
    } finally {
      this.loading = false;
    }
  }

  async loadActions(page = this.actions.page): Promise<boolean> {
    this.loading = true;
    this.error = "";
    try {
      const data = await this.fetchPaginated<AdminAction>(
        "/admin/actions",
        page,
        this.actions.pageSize,
      );
      if (!data) {
        return false;
      }
      this.actions = data;
      return true;
    } catch (e) {
      this.error = e instanceof Error ? e.message : "Failed to load actions";
      return false;
    } finally {
      this.loading = false;
    }
  }

  async loadRequestDetail(id: string): Promise<boolean> {
    if (!this.token) {
      return false;
    }
    this.loading = true;
    this.error = "";
    try {
      const response = await fetch(`/admin/trial-requests/${id}`, {
        headers: this.authHeaders(),
      });
      if (response.status === 401) {
        this.logout();
        throw new Error("Session expired. Please request a new PIN.");
      }
      if (!response.ok) {
        throw new Error("Failed to load trial request");
      }
      this.selectedRequest = await response.json();
      return true;
    } catch (e) {
      this.error = e instanceof Error ? e.message : "Failed to load trial request";
      return false;
    } finally {
      this.loading = false;
    }
  }

  async loadLeadDetail(email: string): Promise<boolean> {
    if (!this.token) {
      return false;
    }
    this.loading = true;
    this.error = "";
    try {
      const response = await fetch(`/admin/leads/${encodeURIComponent(email)}`, {
        headers: this.authHeaders(),
      });
      if (response.status === 401) {
        this.logout();
        throw new Error("Session expired. Please request a new PIN.");
      }
      if (!response.ok) {
        throw new Error("Failed to load lead");
      }
      this.selectedLead = await response.json();
      return true;
    } catch (e) {
      this.error = e instanceof Error ? e.message : "Failed to load lead";
      return false;
    } finally {
      this.loading = false;
    }
  }

  selectAction(action: AdminAction) {
    this.selectedAction = action;
  }
}
